import { createTheme, type ThemeOptions } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { colors } from '@ovComponents/legacyColor';
import convert from 'color-convert';
import type { Theme as HephaestusTheme } from 'hephaestus/core';
import { createMuiSpacing, HephaestusProvider, useTheme } from 'hephaestus/web';
import { type ReactNode, useMemo } from 'react';

declare module '@mui/material/Typography' {
  // biome-ignore lint/style/useConsistentTypeDefinitions: override module
  interface TypographyPropsVariantOverrides {
    notesUserTitle: true;
    notesFooter: true;
    wizardTitle: true;
    wizardSubTitle: true;
  }
}

const getMuiThemeOptions = (hephaestusTheme: HephaestusTheme): ThemeOptions => {
  const rgb = convert.hex.rgb(hephaestusTheme.color.background || '#000000');
  return {
    spacing: createMuiSpacing(hephaestusTheme.spacing),
    typography: {
      fontFamily: hephaestusTheme.font.base,
    },
    palette: {
      primary: {
        main: hephaestusTheme.color.primary,
      },
      secondary: {
        main: colors.secondary,
      },
      success: {
        main: hephaestusTheme.color.positive,
      },
      error: {
        main: hephaestusTheme.color.negative,
      },
      background: {
        default: hephaestusTheme.color.background,
      },
    },
    components: {
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 0,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          InputLabelProps: { shrink: true },
        },
        styleOverrides: {
          root: {
            '&.white': {
              borderColors: 'white',
              '& .MuiInputBase-input': {
                color: 'white',
              },
              '& fieldset': {
                borderColor: 'white',
              },
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: hephaestusTheme.borderRadius.xxl,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.85)`,
          },
          invisible: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '100px',
            paddingLeft: '16px',
            paddingRight: '16px',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            p: 10,
            marginLeft: 8,
            marginRight: 8,
            minWidth: 0,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            paddingTop: 8,
            paddingBottom: 8,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0px -4px 25px rgba(0, 0, 0, 0.05)',
            borderRadius: '4px',
          },
        },
      },
      MuiSnackbar: {
        defaultProps: {
          autoHideDuration: 5000,
        },
        styleOverrides: {
          root: {
            marginTop: '32px',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          icon: {
            fontSize: 14,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            '&.button-disclaimer-responsive': {
              display: 'block',
              textAlign: 'center',
              marginBottom: '16px',
            },
          },
        },
        variants: [
          {
            props: { variant: 'notesFooter' },
            style: {
              fontSize: 12,
              fontWeight: 500,
              fontFamily: hephaestusTheme.font.base,
              lineHeight: '14.4px',
              fontStyle: 'normal',
              display: 'block',
            },
          },
          {
            props: { variant: 'notesUserTitle' },
            style: {
              fontSize: 14,
              fontWeight: 700,
              fontFamily: hephaestusTheme.font.base,
              lineHeight: '16.8px',
              fontStyle: 'normal',
              display: 'block',
            },
          },
          {
            props: { variant: 'wizardTitle' },
            style: {
              fontSize: 28,
              fontWeight: 600,
              color: colors.wizardTitle,
              fontFamily: hephaestusTheme.font.base,
              lineHeight: '34px',
              fontStyle: 'normal',
              display: 'block',
            },
          },
          {
            props: { variant: 'wizardSubTitle' },
            style: {
              fontSize: 24,
              fontWeight: 500,
              color: colors.black,
              fontFamily: hephaestusTheme.font.base,
              lineHeight: '20px',
              fontStyle: 'normal',
              display: 'block',
            },
          },
        ],
      },
    },
  };
};

type ThemeProviderProps = {
  children: ReactNode;
};

const AppMuiThemeProvider = ({ children }: ThemeProviderProps) => {
  const hephaestusTheme = useTheme();
  const muiTheme = useMemo(
    () => createTheme(getMuiThemeOptions(hephaestusTheme)),
    [hephaestusTheme],
  );

  return <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>;
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => (
  <HephaestusProvider>
    <AppMuiThemeProvider>{children}</AppMuiThemeProvider>
  </HephaestusProvider>
);

export default ThemeProvider;
